// ============================================================
// PREZENCE — Prezy, the floating AI HR assistant
// Launcher button (bottom-right) + chat panel. Calls
// /api/assistant/chat. Role scoping is server-side, so this
// same widget is safe for employees, HR, and admin.
// ============================================================
import React, { useState, useRef, useEffect } from 'react';
import api from '../../api/client';
import './prezy.css';

const GREETING = "Hi, I'm Prezy 👋 your Prezence assistant. Ask me about your leave balance, attendance, or company policy.";

export default function Prezy() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', text: GREETING }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, open, sending]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const next = [...messages, { role: 'user', text }];
    setMessages(next);
    setInput('');
    setSending(true);
    try {
      // Send recent history (exclude the greeting) for context.
      const history = next.filter((m, i) => i > 0).slice(-6).map(m => ({ role: m.role, text: m.text }));
      const res = await api.post('/assistant/chat', { message: text, history });
      setMessages(m => [...m, { role: 'assistant', text: res.data.reply }]);
    } catch (e) {
      const msg = e.response?.data?.error || "I couldn't reach the assistant. Please try again.";
      setMessages(m => [...m, { role: 'assistant', text: msg }]);
    } finally {
      setSending(false);
    }
  };

  const onKey = e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <>
      {/* Launcher */}
      <button
        className={`prezy-fab ${open ? 'prezy-fab-open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-label={open ? 'Close Prezy' : 'Open Prezy assistant'}
      >
        {open ? '✕' : <span className="prezy-fab-icon">💬</span>}
      </button>

      {/* Panel */}
      {open && (
        <div className="prezy-panel" role="dialog" aria-label="Prezy assistant">
          <div className="prezy-head">
            <div className="prezy-head-badge">P</div>
            <div className="prezy-head-info">
              <div className="prezy-head-name">Prezy</div>
              <div className="prezy-head-sub">AI HR Assistant</div>
            </div>
            <button className="prezy-close" onClick={() => setOpen(false)} aria-label="Close">✕</button>
          </div>

          <div className="prezy-body" ref={bodyRef}>
            {messages.map((m, i) => (
              <div key={i} className={`prezy-msg ${m.role === 'user' ? 'prezy-msg-user' : 'prezy-msg-bot'}`}>
                {m.text}
              </div>
            ))}
            {sending && (
              <div className="prezy-msg prezy-msg-bot prezy-typing">
                <span /><span /><span />
              </div>
            )}
          </div>

          <div className="prezy-input-row">
            <input
              ref={inputRef}
              className="prezy-input"
              placeholder="Ask Prezy…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKey}
              disabled={sending}
            />
            <button className="prezy-send" onClick={send} disabled={sending || !input.trim()} aria-label="Send">
              ➤
            </button>
          </div>
          <div className="prezy-foot">Prezy can make mistakes — verify important details with HR.</div>
        </div>
      )}
    </>
  );
}
